<script setup>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const selectedCampaign = ref(null);
const togglingCampaignId = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const apiCampaigns = computed(() => getters['campaigns/getApiCampaigns'].value);

const hasNoApiCampaigns = computed(
  () => apiCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleCreate = () => {
  router.push({ name: 'campaigns_api_create', params: route.params });
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleEdit = campaign => {
  router.push({
    name: 'campaigns_api_edit',
    params: { ...route.params, campaignId: campaign.id },
  });
};

const handleViewLogs = campaign => {
  router.push({
    name: 'campaigns_api_dispatch_logs',
    params: { ...route.params, campaignId: campaign.id },
  });
};

const dispatchingRemainingIds = ref(new Set());

const handleDispatchRemaining = async campaign => {
  // Guard against double-clicks / duplicate requests that would race the queue
  // build and create duplicate dispatches.
  if (dispatchingRemainingIds.value.has(campaign.id)) {
    return;
  }
  dispatchingRemainingIds.value.add(campaign.id);
  try {
    const { data } = await window.axios.post(
      `/api/v1/accounts/${route.params.accountId}/campaigns/${campaign.id}/dispatch_remaining`
    );
    const queued = data?.meta?.queued_logs_count ?? 0;
    useAlert(
      queued > 0
        ? `${queued} disparo(s) enfileirado(s) para contatos ainda não enviados.`
        : 'Nenhum contato pendente — todos já foram enviados ou estão na fila.'
    );
    await store.dispatch('campaigns/get');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível disparar os contatos restantes.'
    );
  } finally {
    dispatchingRemainingIds.value.delete(campaign.id);
  }
};

const handleDuplicate = campaign => {
  // Open the create form pre-filled with this campaign's data (a new draft).
  // Nothing is persisted until the user saves, so cancelling leaves no copy.
  router.push({
    name: 'campaigns_api_create',
    params: route.params,
    query: { duplicateFrom: campaign.id },
  });
};

const handleToggleEnabled = async campaign => {
  togglingCampaignId.value = campaign.id;

  try {
    await store.dispatch('campaigns/update', {
      id: campaign.id,
      enabled: !campaign.enabled,
    });

    useAlert(
      campaign.enabled
        ? t('CAMPAIGN.SMS.CARD.BUTTONS.PAUSE_SUCCESS')
        : t('CAMPAIGN.SMS.CARD.BUTTONS.ACTIVATE_SUCCESS')
    );
  } catch {
    useAlert(t('CAMPAIGN.SMS.CARD.BUTTONS.TOGGLE_ERROR'));
  } finally {
    togglingCampaignId.value = null;
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.API.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.API.NEW_CAMPAIGN')"
    @click="handleCreate"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoApiCampaigns"
      :campaigns="apiCampaigns"
      can-toggle
      :toggling-campaign-id="togglingCampaignId"
      @edit="handleEdit"
      @delete="handleDelete"
      @view-logs="handleViewLogs"
      @toggle-enabled="handleToggleEnabled"
      @duplicate="handleDuplicate"
      @dispatch-remaining="handleDispatchRemaining"
    />
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.API.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.API.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
